import CloseIcon from '@/assets/icons/Close';
import MenuIcon from '@/assets/icons/Menu';
import {useHeaderData, useIsActive} from '@/components/globals/Header/HeaderDataProvider';
import LanguageSwitcher from '@/components/globals/Header/LanguageSwitcher';
import NavigationLink from "@/components/NavigationLink";
import {Accordion, AccordionContent, AccordionItem, AccordionTrigger} from '@/components/ui/accordion';
import {Button} from '@/components/ui/button';
import {ScrollArea} from '@/components/ui/scroll-area';
import {Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger} from '@/components/ui/sheet';
import {cn} from '@/lib/utils';
import {useParams, useRouter} from 'next/navigation';
import {useId, useState, useTransition} from 'react';

const baseClassName =
    'border-none text-background text-xl font-semibold py-4 [&_svg]:text-current [&_svg]:size-6 hover:[&_a]:text-secondary [&_a]:transition-colors';

const Item = props => {
    const id = useId();
    const router = useRouter();
    const [_, startTransition] = useTransition();
    const {locale} = useParams();
    const {link, label, columns = [], onClose} = props;

    const isActive = useIsActive({columns, link});

    const showMoreIcon = !!columns.length;

    if (!showMoreIcon)
        return (
            <AccordionItem value={id} className={cn(baseClassName, 'flex px-4')} onClick={onClose}>
                <NavigationLink
                    href={link}
                    className={cn('w-full text-lg xs:text-xl ', {
                        'text-secondary': isActive
                    })}
                >
                    {label}
                </NavigationLink>
            </AccordionItem>
        );

    return (
        <AccordionItem
            value={id}
            className={cn(baseClassName, 'h-fit px-4 data-[state=open]:bg-destructive-foreground transition-colors')}
        >
            <AccordionTrigger
                className={cn(baseClassName, 'py-0 text-lg xs:text-xl hover:text-secondary', {
                    'text-secondary': isActive
                })}
            >
                {label}
            </AccordionTrigger>
            <AccordionContent asChild className='pb-0 mt-4'>
                <div className=' flex flex-col gap-4'>
                    {columns.map((column, index) => (
                        <div key={index} className='flex flex-col pb-2'>
                            {column.label &&
                                <p className='text-lg border-b border-secondary pb-1 mb-2'>{column.label}</p>}
                            <div className='flex flex-col'>
                                {column.children.map((child, index) => {
                                    // TODO these conditions will be removed in future

                                    if (locale !== 'en' && child?.link?.startsWith('/education')) {
                                        return <></>;
                                    }

                                    // TODO these conditions will be removed in future

                                    return (
                                        <NavigationLink href={child.link} key={index} onClick={onClose}>
                                            <p className='text-lg py-2 font-normal'>{child.title}</p>
                                        </NavigationLink>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </AccordionContent>
        </AccordionItem>
    );
};

export default function Menu({links = []}) {
    const [open, setOpen] = useState(false);
    const {loginCta, signUpCta, languages, labels} = useHeaderData();

    const onClose = () => {
        setOpen(false);
    };

    const Icon = open ? CloseIcon : MenuIcon;

    return (
        <div className='flex justify-end'>
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger className='lg:hidden xl:translate-x-4 px-1 -mr-1 hover:bg-transparent'>
                    <Icon
                        className={cn('text-white size-8', {
                            'text-primary': open
                        })}
                    />
                    <span className='sr-only'>Menu Button</span>
                </SheetTrigger>
                <SheetContent className='bg-white px-0 w-5/6' side='left'>
                    <SheetTitle className='sr-only'>Menu</SheetTitle>
                    <SheetDescription className='sr-only'>Open Menu</SheetDescription>
                    <ScrollArea className='pt-3 h-full' classNames={{thumb: 'rounded-none bg-zinc-300'}}>
                        <div className='flex flex-col h-full'>
                            <div className='flex flex-wrap mb-4 gap-2.5 [&_svg]:hidden px-4 h-fit'>
                                {signUpCta && (
                                    <Button
                                        {...signUpCta}
                                        variant='secondary'
                                        className='flex-1 min-w-32'
                                        size='lg'
                                        onClick={() => setOpen(false)}
                                    />
                                )}
                                {loginCta && (
                                    <Button {...loginCta} className='flex-1 min-w-32' size='lg'
                                            onClick={() => setOpen(false)}/>
                                )}
                            </div>

                            <Accordion type='single' collapsible className='w-full'>
                                {links.map((item, index) => (
                                    <Item key={index} {...item} onClose={onClose}/>
                                ))}
                                <LanguageSwitcher labels={labels} languages={languages}/>
                            </Accordion>
                        </div>
                    </ScrollArea>
                </SheetContent>
            </Sheet>
        </div>
    );
}
